import AxiosInstance from "#/redux/api/apiInstance";
import { constructParameters } from "#/utils/APIUtils";

const ongoingRequests = {};

// Helper function to strip query parameters
const stripQueryParams = (url) => url.split("?")[0];

// in-flight dedupe store
const inFlight = new Map();

/**
 * Helper logging function for ApiClient
 *
 * @param  {...any} args
 */
function log(...args) {
  // eslint-disable-next-line no-console
  console.debug(...["ApiClient", ...args]);
}

export class ApiClient {
  /**
   * Dedupe GET requests by URL (or by base URL if stripQueryParamsForKey = true).
   * If same request is already running, returns the same promise (no new network call).
   *
   * NOTE: does NOT cancel previous; it *reuses* it.
   */
  getByUrl(fullUrl, stripQueryParamsForKey = false) {
    const key = stripQueryParamsForKey ? stripQueryParams(fullUrl) : fullUrl;

    if (inFlight.has(key)) return inFlight.get(key);

    const options = {
      method: "GET",
      url: fullUrl,
      params: {}, // url already contains query string if any
    };

    const p = this.callAxios(options, key).finally(() => {
      // cleanup only if still same promise
      if (inFlight.get(key) === p) inFlight.delete(key);
    });

    inFlight.set(key, p);
    return p;
  }

  get(
    url,
    params = undefined,
    page = undefined,
    filter = undefined,
    sort = undefined,
    stripQueryParamsForCancelPrevious = false,
    reuseInFlight = true // NEW (optional)
  ) {
    const parameters = constructParameters(page, filter, sort);
    if (parameters.length > 0) {
      url += "?" + parameters;
    }

    // Reuse ONLY if *exact same* request (use full url as key).
    //
    // `params` are handed to axios separately and never reach `url`, so a
    // url-only key would dedupe two genuinely different requests into one and
    // serve the first one's response to both (e.g. same endpoint, different
    // maxGeneracija). Fold them into the key; callers without params keep the
    // exact key they had before.
    const reuseKey = params ? `${url}|${JSON.stringify(params)}` : url;
    if (reuseInFlight && inFlight.has(reuseKey)) {
      return inFlight.get(reuseKey);
    }

    // Cancel previous request if it exists (your existing logic)
    const baseUrl = stripQueryParamsForCancelPrevious
      ? stripQueryParams(url)
      : url;

    if (ongoingRequests[baseUrl]) {
      ongoingRequests[baseUrl].abort();
    }

    const controller = new AbortController();
    ongoingRequests[baseUrl] = controller;

    const options = {
      method: "GET",
      url: url,
      params: { ...params },
      signal: controller.signal,
    };

    const p = this.callAxios(options, baseUrl).finally(() => {
      // cleanup inflight
      if (inFlight.get(reuseKey) === p) inFlight.delete(reuseKey);
    });

    if (reuseInFlight) inFlight.set(reuseKey, p);
    return p;
  }

  post(url, data, options = {}) {
    const controller = new AbortController();
    ongoingRequests[url] = controller;

    const requestOptions = {
      method: "POST",
      url: url,
      data: data,
      signal: controller.signal, // Include the signal in the options
      ...options,
    };

    return this.callAxios(requestOptions, url);
  }

  put(url, data) {
    // Cancel previous request if it exists
    if (ongoingRequests[url]) {
      ongoingRequests[url].abort();
    }

    const controller = new AbortController();
    ongoingRequests[url] = controller;

    const options = {
      method: "PUT",
      url: url,
      data: data,
      signal: controller.signal, // Include the signal in the options
    };

    return this.callAxios(options, url);
  }

  delete(url, params) {
    if (ongoingRequests[url]) {
      ongoingRequests[url].abort();
    }

    const controller = new AbortController();
    ongoingRequests[url] = controller;

    const options = {
      method: "DELETE",
      url: url,
      params: {
        ...params,
      },
      signal: controller.signal, // Include the signal in the options
    };

    return this.callAxios(options, url);
  }

  getFile(url, params = {}) {
    const options = {
      method: "GET",
      url: url,
      responseType: "blob",
      params: {
        ...params,
      },
      headers: {
        Accept: "application/octet-stream",
      },
    };

    return this.callAxios(options);
  }

  postForFile(url, data, params = {}, requestOptions = {}) {
    const options = {
      method: "POST",
      url: url,
      data: data,
      responseType: "blob",
      params: {
        ...params,
      },
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
      ...requestOptions,
    };

    return this.callAxios(options);
  }

  uploadFile(url, file) {
    const formData = new FormData();
    formData.append("file", file, file.name);

    const options = {
      method: "POST",
      url: url,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
    return this.callAxios(options);
  }

  /**
   * Improved uploader with more flexible parameters
   * @returns
   */
  upload(url, blob, dto = {}, params = {}, headers = {}, method = "POST") {
    if (!(blob instanceof Blob)) {
      throw new Error("Blob must be an instance of Blob");
    }

    // construct the payload for file upload, which includes
    // the file blob and any associated metadata
    const data = new FormData();
    data.append("file", blob); // use blob name if available, otherwise default to "upload"
    data.append("dto", JSON.stringify(dto));

    const req = {
      method: method,
      url,
      data,
      params,
      responseType: "json",
      headers: {
        // ...headers,

        "Content-Type": "multipart/form-data",
        Accept: "application/json",
      },
    };

    return this.callAxios(req);
  }

  request(method, url, data, params, responseType) {
    // Cancel previous request if it exists
    if (ongoingRequests[url]) {
      ongoingRequests[url].abort();
    }

    const controller = new AbortController();
    ongoingRequests[url] = controller;

    const options = {
      method: method,
      url: url,
      data: data,
      responseType: responseType,
      params: {
        ...params,
      },
      signal: controller.signal,
    };

    return this.callAxios(options, url);
  }

  /**
   * Same behavior, but:
   * - optional `cleanupKey` lets us remove abort controllers to avoid leaks
   */
  callAxios(options, cleanupKey) {
    log("making API call with options: ", options);
    try {
      return AxiosInstance.request(options);
    } finally {
      // cleanup abort controller if this call used one of our tracked keys
      if (cleanupKey && ongoingRequests[cleanupKey]) {
        // only remove if it wasn't overwritten by a newer request
        // (best-effort; minimal-change)
        if (options.signal === ongoingRequests[cleanupKey].signal) {
          delete ongoingRequests[cleanupKey];
        }
      }
    }
  }
}

export default new ApiClient();
